/**
 * Organizations listing registry (BAL-664) — the first consumer of
 * `defineListing`. Single source for the org directory's facets, sorts, and
 * search. Consumed by `get-organizations.ts` (via applyListingFilters) and the
 * directory filter bar (via toFilterConfig).
 *
 * Behavior parity with the pre-registry loader: default sort is score DESC NULLS
 * LAST → name → id (the exact order-by BAL-663 shipped); type is single-select
 * eq; reach is multi-select in.
 */
import type { Database } from '~/lib/database.types';
import { defineListing } from '../define-listing';

type OrganizationsRow = Database['public']['Tables']['organizations']['Row'];

/** Type options (no 'all' — an empty facet selection means every type). */
const TYPE_OPTIONS = [
  { value: 'school', labelKey: 'organizations:types.school' },
  { value: 'company', labelKey: 'organizations:types.company' },
  { value: 'course', labelKey: 'organizations:types.course' },
  { value: 'competition', labelKey: 'organizations:types.competition' },
  { value: 'foundation', labelKey: 'organizations:types.foundation' },
  { value: 'brand', labelKey: 'organizations:types.brand' },
  { value: 'service', labelKey: 'organizations:types.service' },
] as const;

const REACH_OPTIONS = [
  { value: 'international', labelKey: 'organizations:reach.international' },
  { value: 'national', labelKey: 'organizations:reach.national' },
  { value: 'local', labelKey: 'organizations:reach.local' },
] as const;

export const organizationsListing = defineListing<OrganizationsRow>({
  table: 'organizations',
  search: { columns: ['name'] },
  facets: [
    {
      key: 'type',
      kind: 'dropdown',
      column: 'organization_type',
      predicate: 'eq',
      options: TYPE_OPTIONS,
    },
    {
      key: 'reach',
      kind: 'toggle',
      column: 'organization_reach',
      predicate: 'in',
      multi: true,
      options: REACH_OPTIONS,
    },
  ],
  sorts: [
    {
      value: 'score',
      labelKey: 'organizations:sort.topRated',
      // Parity with BAL-663: score first (NULLS LAST keeps the 804 unscored
      // below the rated set), then name, then id (stable offset pagination).
      orderBy: [
        { column: 'score', ascending: false, nullsFirst: false },
        { column: 'name', ascending: true },
        { column: 'id', ascending: true },
      ],
    },
    {
      value: 'name',
      labelKey: 'organizations:sort.nameAsc',
      orderBy: [
        { column: 'name', ascending: true },
        { column: 'id', ascending: true },
      ],
    },
  ],
  defaultSort: 'score',
});
